/**
 * In Store Event Landing Page Editor
 * 
 * Allows Tammy to edit landing page content for in-store events
 * Similar to Homepage Editor but focused on a single event
 */

import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  HiSave,
  HiRefresh,
  HiPhotograph,
  HiDocumentText,
  HiTag,
  HiGlobe,
  HiX,
} from 'react-icons/hi';
import ImageSelector from '../components/ImageSelector';
import { adminFetch, adminHeaders } from '../lib/adminApi';

interface LandingPageContent {
  hero?: {
    title?: string;
    description?: string;
    image_url?: string;
  };
  sections?: Array<{
    type: string;
    title?: string;
    content?: string;
    items?: Array<{
      title: string;
      description: string;
      icon?: string;
    }>;
  }>;
  pricing?: {
    starting_at?: number;
    price_text?: string;
    pricing_details?: string;
  };
  cta?: {
    title?: string;
    description?: string;
    button_text?: string;
    button_link?: string;
  };
  seo?: {
    meta_title?: string;
    meta_description?: string;
    meta_keywords?: string;
    og_image_url?: string;
  };
  gallery_images?: string[];
}

interface EventCategory {
  id: string;
  name: string;
  slug: string;
  description: string | null;
  image_url: string | null;
  has_landing_page: boolean;
  landing_page_content: LandingPageContent | null;
}

type TabType = 'hero' | 'sections' | 'pricing' | 'gallery' | 'cta' | 'seo';

export default function EventCategoryEditor() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<TabType>('hero');
  const [category, setCategory] = useState<EventCategory | null>(null);
  const [content, setContent] = useState<LandingPageContent>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!id) return;
    fetchCategory();
  }, [id]);

  const fetchCategory = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await adminFetch(`/api/event-categories/${id}`);
      if (!response.ok) throw new Error('Failed to fetch category');
      const data = await response.json();
      setCategory(data.category);
      setContent(data.category.landing_page_content || {});
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load category');
    } finally {
      setLoading(false);
    }
  };

  const saveContent = async () => {
    if (!id || !category) return;

    setSaving(true);
    setError(null);
    try {
      const response = await adminFetch(`/api/event-categories/${id}`, {
        method: 'PUT',
        headers: adminHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({
          has_landing_page: true,
          landing_page_content: content,
        }),
      });

      if (!response.ok) throw new Error('Failed to save');
      const data = await response.json();
      setCategory(data.category);
      alert('✅ Landing page content saved!');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const addGalleryImage = () => {
    setContent(prev => ({
      ...prev,
      gallery_images: [...(prev.gallery_images || []), ''],
    }));
  };

  const removeGalleryImage = (index: number) => {
    setContent(prev => {
      const gallery = [...(prev.gallery_images || [])];
      gallery.splice(index, 1);
      return { ...prev, gallery_images: gallery };
    });
  };

  const addSection = (type: 'story' | 'features') => {
    setContent(prev => {
      const sections = [...(prev.sections || [])];
      if (type === 'story') {
        sections.push({ type: 'story', title: '', content: '' });
      } else {
        sections.push({ type: 'features', title: '', items: [] });
      }
      return { ...prev, sections };
    });
  };

  // `sections` is optional; use NonNullable so we can safely type an array element.
  // This prevents TS from thinking `LandingPageContent['sections']` might not support `[0]`.
  const updateSection = (
    index: number,
    updates: Partial<NonNullable<LandingPageContent['sections']>[number]>,
  ) => {
    setContent(prev => {
      const sections = [...(prev.sections || [])];
      sections[index] = { ...sections[index], ...updates };
      return { ...prev, sections };
    });
  };

  const removeSection = (index: number) => {
    setContent(prev => {
      const sections = [...(prev.sections || [])];
      sections.splice(index, 1);
      return { ...prev, sections };
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin w-12 h-12 border-4 border-purple-600 border-t-transparent rounded-full mx-auto mb-4"></div>
          <p className="text-gray-500">Loading...</p>
        </div>
      </div>
    );
  }

  if (!category) {
    return (
      <div className="text-center py-12">
        <p className="text-red-600">Category not found</p>
        <button onClick={() => navigate('/admin/event-categories')} className="btn-primary mt-4">
          Back to Categories
        </button>
      </div>
    );
  }

  const tabs: Array<{ id: TabType; label: string; icon: typeof HiPhotograph }> = [
    { id: 'hero', label: 'Hero', icon: HiPhotograph },
    { id: 'sections', label: 'Sections', icon: HiDocumentText },
    { id: 'pricing', label: 'Pricing', icon: HiTag },
    { id: 'gallery', label: 'Gallery', icon: HiPhotograph },
    { id: 'cta', label: 'CTA', icon: HiTag },
    { id: 'seo', label: 'SEO', icon: HiGlobe },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Edit Landing Page</h1>
          <p className="text-gray-500 mt-1">{category.name}</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={fetchCategory}
            className="p-2 border border-gray-300 rounded-lg hover:bg-gray-50"
            title="Refresh"
          >
            <HiRefresh className="w-5 h-5" />
          </button>
          <button
            onClick={saveContent}
            disabled={saving}
            className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50"
          >
            <HiSave className="w-5 h-5" />
            {saving ? 'Saving...' : 'Save'}
          </button>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-red-700">
          {error}
        </div>
      )}

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="flex space-x-8 overflow-x-auto">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 py-4 border-b-2 font-medium text-sm transition-colors ${
                  activeTab === tab.id
                    ? 'border-purple-600 text-purple-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                <Icon className="w-5 h-5" />
                {tab.label}
              </button>
            );
          })}
        </nav>
      </div>

      {/* Tab Content */}
      <div className="bg-white rounded-xl border p-6">
        {/* Hero Tab */}
        {activeTab === 'hero' && (
          <div className="space-y-4">
            <h3 className="text-lg font-semibold">Hero Section</h3>
            {/* Image size hints: must stay in sync with public EventCategoryLandingPage hero (full width × h-80 md:h-[500px], object-cover). */}
            <div
              className="rounded-lg border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-900"
              role="note"
              aria-label="Image size tips for the hero banner"
            >
              <p className="font-semibold text-blue-950 mb-2">Hero image — recommended size</p>
              <ul className="list-disc list-inside space-y-1 text-blue-900/90">
                <li>
                  <strong>Shape:</strong> wide landscape (not portrait). The live page shows this banner as{' '}
                  <strong>full screen width</strong> by <strong>320px tall on phones</strong> and{' '}
                  <strong>500px tall on desktop</strong>, and it <strong>crops</strong> the photo to fill that box (
                  <code className="text-xs bg-blue-100 px-1 rounded">object-cover</code>).
                </li>
                <li>
                  <strong>Pixels to design:</strong> export at least <strong>1920 × 800</strong> pixels ( wider is
                  fine, e.g. <strong>2400 × 1000</strong> ). That stays sharp on large monitors.
                </li>
                <li>
                  <strong>Safe zone:</strong> keep cake, text-in-image, and logos in the <strong>middle 60% wide</strong>{' '}
                  and <strong>middle 50% tall</strong> so cropping on phones and wide screens does not cut off the
                  important part.
                </li>
                <li>
                  <strong>File:</strong> WebP or JPEG; keep file size reasonable so the page loads fast on mobile data.
                </li>
              </ul>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Hero Title (optional - defaults to category name)
              </label>
              <input
                type="text"
                value={content.hero?.title || ''}
                onChange={(e) =>
                  setContent(prev => ({
                    ...prev,
                    hero: { ...prev.hero, title: e.target.value },
                  }))
                }
                className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                placeholder={category.name}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Hero Description
              </label>
              <textarea
                value={content.hero?.description || ''}
                onChange={(e) =>
                  setContent(prev => ({
                    ...prev,
                    hero: { ...prev.hero, description: e.target.value },
                  }))
                }
                rows={4}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                placeholder={category.description || 'Enter description...'}
                aria-label="Hero description"
                title="Hero description"
              />
            </div>
            <ImageSelector
              value={content.hero?.image_url || ''}
              onChange={(url) =>
                setContent(prev => ({
                  ...prev,
                  hero: { ...prev.hero, image_url: url },
                }))
              }
              label="Hero Image"
              helpText="Full-width banner at the top of /events/… — use a wide photo (see tips above). Title and description you type below appear on top of this image."
              previewHeight="h-52"
              previewAspect="wide"
            />
          </div>
        )}

        {/* Sections Tab */}
        {activeTab === 'sections' && (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold">Content Sections</h3>
              <div className="flex gap-2">
                <button
                  onClick={() => addSection('story')}
                  className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  + Story Section
                </button>
                <button
                  onClick={() => addSection('features')}
                  className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  + Features Section
                </button>
              </div>
            </div>
            {(content.sections || []).map((section, index) => (
              <div key={index} className="border border-gray-200 rounded-lg p-4">
                <div className="flex items-center justify-between mb-4">
                  <span className="text-sm font-medium text-gray-700">
                    {section.type === 'story' ? 'Story Section' : 'Features Section'}
                  </span>
                  <button
                    onClick={() => removeSection(index)}
                    className="text-red-600 hover:text-red-700"
                    aria-label="Remove section"
                    title="Remove section"
                  >
                    <HiX className="w-5 h-5" />
                  </button>
                </div>
                <div className="space-y-4">
                  <input
                    type="text"
                    value={section.title || ''}
                    onChange={(e) => updateSection(index, { title: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                    placeholder="Section title"
                  />
                  {section.type === 'story' && (
                    <textarea
                      value={section.content || ''}
                      onChange={(e) => updateSection(index, { content: e.target.value })}
                      rows={6}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                      placeholder="Story content..."
                      aria-label="Story content"
                      title="Story content"
                    />
                  )}
                  {section.type === 'features' && (
                    <div className="space-y-2">
                      <p className="text-sm text-gray-600">Features (add items manually in JSON for now)</p>
                      <textarea
                        value={JSON.stringify(section.items || [], null, 2)}
                        onChange={(e) => {
                          try {
                            const items = JSON.parse(e.target.value);
                            updateSection(index, { items });
                          } catch {
                            /* ignore invalid JSON */
                          }
                        }}
                        rows={8}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg font-mono text-sm"
                        placeholder='[{"title": "Feature 1", "description": "...", "icon": "🎂"}]'
                        aria-label="Features JSON"
                        title="Features JSON"
                      />
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Pricing Tab */}
        {activeTab === 'pricing' && (
          <div className="space-y-4">
            <h3 className="text-lg font-semibold">Pricing Information</h3>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Starting Price ($)
              </label>
              <input
                type="number"
                step="0.01"
                value={content.pricing?.starting_at || ''}
                onChange={(e) =>
                  setContent(prev => ({
                    ...prev,
                    pricing: { ...prev.pricing, starting_at: parseFloat(e.target.value) || undefined },
                  }))
                }
                className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                placeholder="50.00"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Price Text
              </label>
              <input
                type="text"
                value={content.pricing?.price_text || ''}
                onChange={(e) =>
                  setContent(prev => ({
                    ...prev,
                    pricing: { ...prev.pricing, price_text: e.target.value },
                  }))
                }
                className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                placeholder="Starting at $50"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Pricing Details
              </label>
              <textarea
                value={content.pricing?.pricing_details || ''}
                onChange={(e) =>
                  setContent(prev => ({
                    ...prev,
                    pricing: { ...prev.pricing, pricing_details: e.target.value },
                  }))
                }
                rows={4}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                placeholder="Additional pricing information..."
                aria-label="Pricing details"
                title="Pricing details"
              />
            </div>
          </div>
        )}

        {/* Gallery Tab */}
        {activeTab === 'gallery' && (
          <div className="space-y-4">
            <p className="text-sm text-gray-600 rounded-lg border border-gray-200 bg-gray-50 px-3 py-2">
              <strong>Gallery image size:</strong> on the public page each thumbnail is a <strong>square</strong>. Use
              at least <strong>800 × 800</strong> px ( <strong>1200 × 1200</strong> for sharper screens). Center your
              subject; edges may crop slightly.
            </p>
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold">Gallery Images</h3>
              <button
                onClick={addGalleryImage}
                className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                + Add Image
              </button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {(content.gallery_images || []).map((imageUrl, index) => (
                <div key={index} className="border border-gray-200 rounded-lg p-4 relative">
                  <button
                    onClick={() => removeGalleryImage(index)}
                    className="absolute top-2 right-2 p-1 bg-red-500 text-white rounded-full hover:bg-red-600 z-10"
                    aria-label="Remove gallery image"
                    title="Remove gallery image"
                  >
                    <HiX className="w-4 h-4" />
                  </button>
                  <ImageSelector
                    value={imageUrl}
                    onChange={(url) => {
                      const gallery = [...(content.gallery_images || [])];
                      gallery[index] = url;
                      setContent(prev => ({ ...prev, gallery_images: gallery }));
                    }}
                    label={`Gallery Image ${index + 1}`}
                    showPreview={true}
                    previewHeight="h-32"
                    previewAspect="square"
                    allowClear={false}
                  />
                </div>
              ))}
            </div>
          </div>
        )}

        {/* CTA Tab */}
        {activeTab === 'cta' && (
          <div className="space-y-4">
            <h3 className="text-lg font-semibold">Call to Action</h3>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                CTA Title
              </label>
              <input
                type="text"
                value={content.cta?.title || ''}
                onChange={(e) =>
                  setContent(prev => ({
                    ...prev,
                    cta: { ...prev.cta, title: e.target.value },
                  }))
                }
                className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                placeholder="Ready to Get Started?"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                CTA Description
              </label>
              <textarea
                value={content.cta?.description || ''}
                onChange={(e) =>
                  setContent(prev => ({
                    ...prev,
                    cta: { ...prev.cta, description: e.target.value },
                  }))
                }
                rows={3}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                placeholder="Contact us to learn more..."
                aria-label="CTA description"
                title="CTA description"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Button Text
              </label>
              <input
                type="text"
                value={content.cta?.button_text || ''}
                onChange={(e) =>
                  setContent(prev => ({
                    ...prev,
                    cta: { ...prev.cta, button_text: e.target.value },
                  }))
                }
                className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                placeholder="Book Now"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Button Link
              </label>
              <input
                type="text"
                value={content.cta?.button_link || ''}
                onChange={(e) =>
                  setContent(prev => ({
                    ...prev,
                    cta: { ...prev.cta, button_link: e.target.value },
                  }))
                }
                className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                placeholder="/contact"
              />
            </div>
          </div>
        )}

        {/* SEO Tab */}
        {activeTab === 'seo' && (
          <div className="space-y-4">
            <h3 className="text-lg font-semibold">SEO Settings</h3>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Meta Title
              </label>
              <input
                type="text"
                value={content.seo?.meta_title || ''}
                onChange={(e) =>
                  setContent(prev => ({
                    ...prev,
                    seo: { ...prev.seo, meta_title: e.target.value },
                  }))
                }
                className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                placeholder={`${category.name} | Nanny B's Bakery`}
              />
              <p className="text-xs text-gray-500 mt-1">
                Current: {content.seo?.meta_title?.length || 0} characters (recommended: 50-60)
              </p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Meta Description
              </label>
              <textarea
                value={content.seo?.meta_description || ''}
                onChange={(e) =>
                  setContent(prev => ({
                    ...prev,
                    seo: { ...prev.seo, meta_description: e.target.value },
                  }))
                }
                rows={3}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                placeholder={category.description || 'Enter SEO description...'}
                aria-label="Meta description"
                title="Meta description"
              />
              <p className="text-xs text-gray-500 mt-1">
                Current: {content.seo?.meta_description?.length || 0} characters (recommended: 150-160)
              </p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Meta Keywords
              </label>
              <input
                type="text"
                value={content.seo?.meta_keywords || ''}
                onChange={(e) =>
                  setContent(prev => ({
                    ...prev,
                    seo: { ...prev.seo, meta_keywords: e.target.value },
                  }))
                }
                className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                placeholder="keyword1, keyword2, keyword3"
              />
            </div>
            <ImageSelector
              value={content.seo?.og_image_url || ''}
              onChange={(url) =>
                setContent(prev => ({
                  ...prev,
                  seo: { ...prev.seo, og_image_url: url },
                }))
              }
              label="OG Image URL"
              helpText="Shown when this page is shared (Facebook, Messages, etc.). Best: 1200 × 630 px (about 1.9:1). Put text and logo in the center safe area."
              previewHeight="h-32"
              previewAspect="video"
            />
          </div>
        )}
      </div>

    </div>
  );
}
